import type { ReactNode, SelectHTMLAttributes } from "react";
import { forwardRef } from "react";

/**
 * Composant Select · même DA que Input.
 * Accepte soit des `children` (les <option>) soit une prop `options` array.
 *
 * Mode `compact` (sans label intégré) : utile pour les barres de filtres
 * où le label est rendu à côté ou pas du tout.
 */
type Option = { value: string; label: string; disabled?: boolean };

type Props = Omit<SelectHTMLAttributes<HTMLSelectElement>, "className"> & {
  label?: string;
  hint?: string;
  error?: string;
  options?: ReadonlyArray<Option>;
  placeholder?: string;
  /** Si true : pas de label/hint affichés, juste le contrôle. */
  compact?: boolean;
  className?: string;
  children?: ReactNode;
};

export const Select = forwardRef<HTMLSelectElement, Props>(function Select(
  {
    label,
    hint,
    error,
    options,
    placeholder,
    compact = false,
    className = "",
    id,
    children,
    ...rest
  },
  ref,
) {
  const selectId = id ?? rest.name;

  const control = (
    <div
      className={`flex items-center gap-2 rounded-xl bg-white pr-3 ring-1 transition-all focus-within:ring-2 ${
        error
          ? "ring-red-300 focus-within:ring-red-500"
          : "ring-navy-200 focus-within:ring-accent-500"
      } ${compact ? "py-1.5 pl-3" : "mt-1.5 px-3.5 py-2.5"}`}
    >
      <select
        id={selectId}
        ref={ref}
        className="w-full appearance-none bg-transparent pr-1 text-navy-800 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
        aria-invalid={error ? "true" : undefined}
        aria-describedby={
          error ? `${selectId}-err` : hint ? `${selectId}-hint` : undefined
        }
        {...rest}
      >
        {placeholder ? (
          <option value="">{placeholder}</option>
        ) : null}
        {options
          ? options.map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </option>
            ))
          : children}
      </select>
      {/* Chevron sobre, ne capture pas le clic */}
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="shrink-0 text-navy-400"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
  );

  if (compact) {
    return <div className={className}>{control}</div>;
  }

  return (
    <div className={`w-full ${className}`}>
      {label ? (
        <label
          htmlFor={selectId}
          className="block text-sm font-semibold text-navy-800"
        >
          {label}
          {rest.required ? (
            <span className="ml-1 text-accent-500" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
      ) : null}
      {control}
      {error ? (
        <p id={`${selectId}-err`} className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={`${selectId}-hint`} className="mt-1.5 text-xs text-navy-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
